import { inject, Injectable } from '@angular/core';
import { CashTitle, Receive } from '../interface/cash-title';
import { ApiCashTitleIn, PaginatedTitle } from './cash-title.service';
import { ClientService } from './client.service';
import { ExpenseService } from './expense.service';
import { PaymentService } from './payment.service';
import { SupplierService } from './supplier.service';
import { TitleTypeService } from './title-type.service';
import { Money } from '../class/money';
import Decimal from 'decimal.js';
import { PaymentCondition } from '../interface/paymentCondition';
import { IPerson } from '../interface/person';
import { ExpenseType } from '../interface/expense-type';
import { TitleType } from '../interface/title-type';
import { LOG_REPORTER_KEYS, LogReporterService } from './log-reporter.service';
import { StatusTitle } from '../component/page/company/records/status-title/models/status-title.model';

@Injectable({
  providedIn: 'root'
})
export class CashTitleHandlerService {

  private expenseTypes: ExpenseType[] = [];
  private paymentConditions: PaymentCondition[] = [];
  private types: TitleType[] = [];
  private persons: IPerson[] = [];

  private expenseService = inject(ExpenseService);
  private supplierService = inject(SupplierService);
  private clientService = inject(ClientService);
  private paymentService = inject(PaymentService);
  private titleTypeService = inject(TitleTypeService);
  private _loggerService = inject(LogReporterService);

  private static getStatusTitleId(statusTitle: string | StatusTitle | { id?: string; _id?: string } | null | undefined): string | null {
    if (!statusTitle) {
      return null;
    }

    if (typeof statusTitle === 'string') {
      return statusTitle;
    }

    if (statusTitle.id) {
      return String(statusTitle.id);
    }

    if (statusTitle?.["_id"]) {
      return String(statusTitle?.["_id"]);
    }

    return null;
  }

  async getData() {
    if (!this.expenseTypes.length) {
      this.expenseTypes = await this.expenseService.getAll()
    }
    if (!this.paymentConditions.length) {
      this.paymentConditions = await this.paymentService.getAll()
    }
    if (!this.types.length) {
      this.types = await this.titleTypeService.getAll()
    }
    if (!this.persons.length) {
      const clients = await this.clientService.getAll();
      const suppliers = await this.supplierService.getAll();
      this.persons = Array.from(clients.values()).concat(suppliers);
    }
  }

  /**
   * Keep the cached collections in sync when an entity is created or edited
   * elsewhere in the app, so `complyAPP()` always resolves the current object.
   *
   * Add-or-replace by id. No-op when the cache is not yet loaded (empty): the
   * first `getData()` will fetch the already-committed change fresh from the API.
   */
  upsertExpenseType(item: ExpenseType): void {
    if (!item?._id || !this.expenseTypes.length) return;
    const i = this.expenseTypes.findIndex(e => e._id === item._id);
    i >= 0 ? (this.expenseTypes[i] = item) : this.expenseTypes.push(item);
  }

  upsertPaymentCondition(item: PaymentCondition): void {
    if (!item?._id || !this.paymentConditions.length) return;
    const i = this.paymentConditions.findIndex(p => p._id === item._id);
    i >= 0 ? (this.paymentConditions[i] = item) : this.paymentConditions.push(item);
  }

  upsertType(item: TitleType): void {
    if (!item?._id || !this.types.length) return;
    const i = this.types.findIndex(t => t._id === item._id);
    i >= 0 ? (this.types[i] = item) : this.types.push(item);
  }

  upsertPerson(item: IPerson): void {
    if (!item?.id || !this.persons.length) return;
    const i = this.persons.findIndex(p => p.id === item.id);
    i >= 0 ? (this.persons[i] = item) : this.persons.push(item);
  }

  /**
   * Transform a API object title into a MekanciWeb CashTitle
   *
   * Make sure to call `getData()` before `complyAPP()`
   */
  complyAPP(apiObject: ApiCashTitleIn): CashTitle {
    const received: Receive[] = apiObject.received || [];
    const cashtitle: CashTitle = {
      _id: apiObject._id,
      idOS: apiObject.idOS,
      groupId: apiObject.groupId,
      companyCode: apiObject.companyCode,
      typeId: apiObject.type,
      supplier_clientId: apiObject.supplier_client,
      expenseTypeId: apiObject.expenseType,
      paymentConditionId: apiObject.paymentCondition,
      invoiceNumber: apiObject.invoiceNumber,
      serial: apiObject.serial,
      invoiceId: apiObject.invoiceId,
      observation: apiObject.observation,
      value: apiObject.value,
      parcel: apiObject.parcel,
      balance: apiObject.balance,
      status: apiObject.status,
      bank: apiObject.bank,
      adiantamento: apiObject.adiantamento === 1,
      received: received,
      fees: received.reduce((sum, low) => sum + (low.fees || 0), 0),
      penaltyFee: received.reduce((sum, low) => sum + (low.penaltyFee || 0), 0),
      rate: received.reduce((sum, low) => sum + (low.rate || 0), 0),
      discount: received.reduce((sum, low) => sum + (low.discount || 0), 0),
      rateTitle: apiObject.rateTitle || 0,
      boleto: apiObject?.boleto,
      totalInstallments: apiObject.totalInstallments,
      currentInstallment: apiObject.currentInstallment,
    }

    if (apiObject.movementDate) {
      // prevent initial Date (1970-01-01)
      cashtitle.movementDate = new Date(apiObject.movementDate);
    }

    if (apiObject.expirationDate) {
      // prevent initial Date (1970-01-01)
      cashtitle.expirationDate = new Date(apiObject.expirationDate)
    }

    if (apiObject.provisional) {
      cashtitle.provisional = apiObject.provisional;
    }

    if (apiObject.clientTmp) {
      cashtitle.clientTmp = apiObject.clientTmp;
    }

    if (apiObject.statusTitle) {
      cashtitle.statusTitle = new StatusTitle(apiObject.statusTitle);
    }

    this.calculateBalance(cashtitle);

    cashtitle.expenseType = this.expenseTypes.find(expense => expense._id === cashtitle.expenseTypeId);

    if (Array.isArray(apiObject.expenseAllocations) && apiObject.expenseAllocations.length) {
      cashtitle.expenseAllocations = apiObject.expenseAllocations.map(allocation => ({
        expenseType: this.expenseTypes.find(expense => expense._id === allocation.expenseType) ?? allocation.expenseType,
        value: allocation.value,
      }));
    }

    cashtitle.paymentCondition = this.paymentConditions.find(payment => payment._id === cashtitle.paymentConditionId);
    cashtitle.type = this.types.find(_type => _type._id === cashtitle.typeId);
    cashtitle.supplier_client = this.persons.find(person => person.id === cashtitle.supplier_clientId);
    // this.calculateBalance(cashtitle)
    return cashtitle
  }

  complyAPI(cashTitle: CashTitle | PaginatedTitle) {

    // reset payment method

    if (Object.hasOwn(cashTitle, 'rateValue')) {
      cashTitle['rateValue'] = undefined
    }

    if (Object.hasOwn(cashTitle, 'feesValue')) {
      cashTitle['feesValue'] = undefined
    }


    if (Object.hasOwn(cashTitle, 'penaltyFeeValue')) {
      cashTitle['penaltyFeeValue'] = undefined
    }

    if (Object.hasOwn(cashTitle, 'parcelValue')) {
      cashTitle['parcelValue'] = undefined
    }

    cashTitle.status = cashTitle.status || 0;
    cashTitle.rateTitle = cashTitle.rateTitle || 0;

    let apiObject: any = { ...cashTitle };
    const hasStatusTitle = Object.prototype.hasOwnProperty.call(apiObject, 'statusTitle');
    const statusTitleValue = apiObject.statusTitle;
    apiObject.adiantamento = Number(cashTitle.adiantamento) || 0;
    // apiObject.type =
    delete apiObject.toPay;
    delete apiObject.parcelValue;

    // removing Mongo properties
    delete apiObject.updatedAt;
    delete apiObject.createdAt;
    delete apiObject.company;
    delete apiObject.seq;
    delete apiObject.__v;
    delete apiObject._id;

    // throw error if have receives with same id
    if (cashTitle.received && Array.isArray(cashTitle.received)) {
      const ids = new Set(cashTitle.received.reduce((arr, receive) => arr.concat(receive._id), []));
      if (ids.size !== cashTitle.received.length) {
        throw new Error("Baixas com identificadores iguais")
      }
    }

    if (cashTitle.movementDate) {
      const movementDate = new Date(cashTitle.movementDate);
      if (movementDate.valueOf()) {
        apiObject.movementDate = movementDate.toISOString()
      }
    }

    if (cashTitle.expirationDate) {
      const expirationDate = new Date(cashTitle.expirationDate);
      if (expirationDate.valueOf()) {
        apiObject.expirationDate = expirationDate.toISOString()
      }
    }

    Object.keys(apiObject).forEach(key => {
      if ([undefined, null, ""].includes(apiObject[key])) {
        delete apiObject[key]
      }
    });

    if (cashTitle.type) {
      if (typeof cashTitle.type === "string") {
        apiObject.type = cashTitle.type;
      } else {
        apiObject.type = cashTitle.type._id;
      }
      delete apiObject.typeId
    } else if (cashTitle['typeId']) {
      apiObject.type = cashTitle['typeId'];
    }

    if (apiObject.expenseTypeId) {
      apiObject.expenseType = apiObject.expenseTypeId;
      delete apiObject.expenseTypeId
    }

    // Rateio de despesa (opcional): normaliza cada parcela para { expenseType: id, value }.
    // Vazio/ausente ⇒ remove o campo, preservando o comportamento do expenseType único.
    if (Array.isArray(apiObject.expenseAllocations) && apiObject.expenseAllocations.length) {
      apiObject.expenseAllocations = apiObject.expenseAllocations
        .map((allocation: any) => ({
          expenseType: typeof allocation?.expenseType === 'string' ? allocation.expenseType : allocation?.expenseType?._id,
          value: allocation?.value,
        }))
        .filter((allocation: any) => allocation.expenseType != null && allocation.value != null);
      if (!apiObject.expenseAllocations.length) {
        delete apiObject.expenseAllocations;
      }
    } else {
      delete apiObject.expenseAllocations;
    }

    if (apiObject.paymentConditionId) {
      apiObject.paymentCondition = apiObject.paymentConditionId;
      delete apiObject.paymentConditionId
    } else if (apiObject.paymentCondition) {
      if (typeof apiObject.paymentCondition == 'string') {
        apiObject.paymentCondition = apiObject.paymentCondition;
      } else {
        apiObject.paymentCondition = (apiObject.paymentCondition as PaymentCondition)._id;
      }
    }

    if (apiObject.supplier_clientId) {
      apiObject.supplier_client = apiObject.supplier_clientId;
      delete apiObject.supplier_clientId
    } else if (apiObject.supplier_client) {
      if (typeof apiObject.supplier_client == 'string') {
        apiObject.supplier_client = apiObject.supplier_client;
      } else {
        apiObject.supplier_client = (apiObject.supplier_client as IPerson).id;
      }
    }

    if (hasStatusTitle) {
      const statusTitleId = CashTitleHandlerService.getStatusTitleId(statusTitleValue);
      apiObject.statusTitle = statusTitleId || null;
    } else {
      delete apiObject.statusTitle;
    }

    const requiredFields = ["type", "paymentCondition", "supplier_client"].filter(field => !(field in apiObject));
    if (requiredFields.length) {
      const warnMessage = `Campos obrigatórios faltando para geração do título: ${requiredFields.join(', ')}`;
      const logger = this._loggerService.textLogger("CashTitleHandlerService.complyAPI()", LOG_REPORTER_KEYS.INVOICE);
      logger(warnMessage);
      console.warn(warnMessage);
    }

    return apiObject
  }

  private calculateFees(toPay: number, cashTitle: CashTitle | PaginatedTitle) {
    const feesValue = toPay ? Money(toPay * (cashTitle.fees / 100)) || 0 : 0;
    return Math.abs(feesValue);
  }

  private calculateRate(toPay: number, cashTitle: CashTitle | PaginatedTitle) {
    const rateValue = toPay ? Money(toPay * (cashTitle.rate / 100)) || 0 : 0;
    return Math.abs(rateValue);
  }

  private calculatePenaltyFee(toPay: number, cashTitle: CashTitle | PaginatedTitle) {
    const penaltyFeeValue = toPay ? Money(toPay * (cashTitle.penaltyFee / 100)) || 0 : 0;
    return Math.abs(penaltyFeeValue);
  }

  calculateBalance(cashTitle: CashTitle | PaginatedTitle) {
    const rateValue = this.calculateFees(cashTitle.balance, cashTitle);
    const feesValue = this.calculatePenaltyFee(cashTitle.balance, cashTitle);
    const penaltyFeeValue = this.calculateRate(cashTitle.balance, cashTitle);

    const toPay = new Decimal(cashTitle.balance ?? 0)
      .plus(rateValue)
      .plus(feesValue)
      .plus(penaltyFeeValue)
      .minus(cashTitle.discount);

    cashTitle.toPay = Money(toPay.toNumber());
  }

}
